import { marked } from "marked";

// Match the backend renderer: no auto-generated header IDs, no email mangling.
marked.use({ mangle: false, headerIds: false });

/**
 * Heuristic: is this content already HTML?
 * Posts created before the Markdown → HTML migration stored Markdown in
 * `content`. Block-level tags mean the content is already HTML and must pass
 * through untouched — running marked.parse() over HTML mangles tables and
 * anchors. Mirrors services/sanity/postMapper.js looksLikeHtml().
 */
export function looksLikeHtml(content) {
  return /<(p|div|h[1-6]|ul|ol|table|blockquote|section|article|figure)\b/i.test(content);
}

/**
 * Resolve a post's renderable HTML body.
 * Prefers an explicit `contentHtml`, passes HTML `content` through unchanged,
 * and converts legacy Markdown posts on the fly.
 *
 * @param {{ contentHtml?: string, content?: string }} post
 * @returns {string} HTML ready for rendering
 */
export function resolveContentHtml(post) {
  const explicit = post?.contentHtml;
  if (explicit && explicit.trim().length > 0) return unwrapRawHtmlBlocks(explicit);

  const content = post?.content || "";
  if (!content.trim()) return "";

  const html = looksLikeHtml(content) ? content : marked.parse(content);
  return unwrapRawHtmlBlocks(html);
}

/**
 * Unwrap <div data-raw-html data-html="..."> blocks — the actual HTML lives in
 * the data-html attribute, HTML-entity-encoded by the editor's serializer.
 */
function unwrapRawHtmlBlocks(html) {
  return html.replace(
    /<div[^>]*data-raw-html[^>]*data-html="([^"]*)"[^>]*>[\s\S]*?<\/div>/g,
    (_, encoded) =>
      encoded
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
  );
}
